import { atom } from "jotai";
import { JotaiTool } from "../../../../index";

export const interfaceSetupToolsAtom = atom<JotaiTool<any>[]>(() => [
  // TODO: Add specific tools for interface setup operations
  // Example: get(networkRequestAtom), get(canvasStateAtom), etc.
]);
interfaceSetupToolsAtom.debugLabel = "interfaceSetupToolsAtom";

export const interfaceSetupRecipe = `# Interface Setup: Create an interface with a form that submits to a Zapier Table

## Overview
Create an interface with a form that submits to a Zapier Table by using the PATCH endpoint to update page content with a form-block containing field-block children.

**IMPORTANT: Field blocks must be children of a form-block, not standalone elements in the blocks array.**

## Key API Endpoints

### POST interfaces/api/interfaces/v0/interfaces?create_home_page=true
- **Purpose**: Create a new interface project with a home page
- **Authentication**: Uses session cookies via network_request tool
- **Required Body**: \`{ "name": "Interface Name" }\` (name: 1-32 chars, alphanumeric/spaces/hyphens only)
- **Example**: \`network_request({ method: "POST", api: "interfaces", path: "/api/interfaces/v0/interfaces?create_home_page=true", body: { name: "My Interface" } })\`

### PATCH interfaces/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}
- **Purpose**: Update page content with form blocks and field mappings to table
- **Authentication**: Uses session cookies via network_request tool
- **Example**: \`network_request({ method: "PATCH", api: "interfaces", path: "/api/interfaces/v0/interfaces/{interface_id}/pages/{page_id}" })\`

## Implementation Steps

1. **Create interface project** using POST /api/interfaces/v0/interfaces?create_home_page=true with basic name
2. **Extract projectId and homepageId** from the response
3. **Update canvas** with interface details (projectId, pageId)
4. **Use PATCH endpoint** to add form content to the page
5. **Structure content** with version 2 and blocks array containing form-block with field-block children
6. **Map form fields to table fields** using tablesFieldKey format (f1, f2, f3, etc.)
7. **Configure form submission** to storageType: 'tables' and storageId: tableId
8. **Add notification trigger** for user feedback (types: 'notification', 'navigate', or 'openExternalUrl')
9. **Create visual connection** between interface and table nodes on canvas

## Critical Patterns

### Form Block Structure
\`\`\`
{
  type: 'form-block',
  config: {
    submitText: 'Submit',
    storageType: 'tables',
    storageId: tableId,
    triggers: [{ 
      type: 'notification',
      config: {
        message: 'Thank you! Your form has been submitted successfully.'
      }
    }]
  },
  children: [field-blocks]
}
\`\`\`

### Field Block Structure
\`\`\`
{
  type: 'field-block',
  config: {
    name: 'fieldName',
    required: true/false,
    label: 'Field Label',
    inputType: 'text'|'email'|'number'|etc,
    tablesFieldKey: 'f1'|'f2'|'f3'|etc
  }
}
\`\`\`

### Page Content Structure
\`\`\`
{
  content: {
    version: '2',
    blocks: [form-block with children]
  }
}
\`\`\`

## ERROR RECOVERY & PERSISTENCE PROTOCOL

**When Recipe Hash Fails:**
- Recipe hashes can be unreliable - this is normal, not a blocker
- If use_recipe fails, immediately continue with direct API approach
- DO NOT interpret recipe hash failure as "lack of guidance"

**When API Calls Fail (405/404/etc):**
- API errors are normal during discovery - not signs to handoff
- 405 errors typically mean wrong HTTP method or endpoint structure
- MUST try minimum 2-3 different API approaches before considering handoff:
  - Try different HTTP methods (PUT, PATCH instead of POST)
  - Try different endpoint patterns (/blocks, /components, /elements)
  - Search for API examples with different terms

**Search Persistence Rules:**
- MUST try minimum 3 different search terms before concluding no APIs exist:
  - "form components", "add blocks", "create form", "page elements"
  - Try both generic and specific terms
- Most interface tasks CAN be accomplished through APIs - exhaust options first

**Confidence Anchor:**
Interfaces are designed to be API-driven. Complex form structures are normal and expected through API calls, not indicators that specialized tools are needed.

## Important Notes

- **Always create interfaces with ?create_home_page=true** to automatically get a page
- **Use PATCH endpoint to update entire page content** - individual block creation endpoints don't exist  
- **Page content must use version '2' format** with blocks array structure
- **Field blocks MUST be children of form-block** - not standalone elements in blocks array
- **Form blocks MUST have submitText field** - this is required by the API
- **Form blocks MUST have at least one trigger** - minItems: 1 per API spec
- **Form submission requires both storageType and storageId** to be properly configured
- **Map table fields using tablesFieldKey format** (f + field_id) to connect to table fields
- **Trigger types must be valid** - use 'notification', 'navigate', or 'openExternalUrl' (NOT 'success-notification')
- **When creating an interface based off of a table**, there's a special endpoint called create interface from table that can be more helpful
- **Interfaces are useless without a page** - always ensure a page is created

## Troubleshooting

### API Validation Errors:
- **"child blocks should be validated in the context of their parent blocks, not directly"**: This 500 error occurs when field blocks are not properly structured as children of form-block. Ensure field blocks are in the children array of the form-block, not in the top-level blocks array
- **"Invalid input" for form-block**: Check that submitText is included and triggers use valid types
- **"Invalid literal value" errors**: Ensure block types match exactly (e.g., 'field-block', 'markdown-text-block')
- **Missing required fields**: Field blocks need name, required, label, and inputType
- **Form block validation**: Must include submitText (string) and triggers (array with minItems: 1)
- **Interface name validation**: Must be 1-32 characters, alphanumeric/spaces/hyphens only (pattern: ^[a-zA-Z0-9 -]*$)

### General Issues:
- **405 Method Not Allowed**: Don't use POST /api/interfaces/v0/blocks - use PATCH page content instead
- **Content not updating**: Use version '2' format with blocks array structure

### Form Block Specific:
- **Field mapping issues**: Ensure exact tablesFieldKey format (f + field_id)
- **Form not submitting**: Verify storageType and storageId are properly configured
- **Invalid trigger types**: Use 'notification', 'navigate', or 'openExternalUrl' only
- **Field blocks not recognized**: Make sure field blocks are in the children array of form-block, not top-level blocks`;
